<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        
        <!-- Buttons to show:  
        - all dynamic ones from possible_transitions? (decline, start) or just subset?
        - Create - if that's available
        - Messages - if we have a response
        - Dummy one for internal people who look at it...
        - Button with link to register for QuickLink views
        -->

        <!-- CREATE BUTTON -->
        <transition name="fade-fast" mode="out-in">
            <b-button
                v-if="showDummyCreateButton || canCreate"
                id="create-response-button"
                :disabled="showDummyCreateButton"
                variant="primary"
                block
                class="text-uppercase py-3 mt-3 shadow-sm" 
                @click="createResponse"
                v-b-tooltip.hover.left
                title="Start your response now"
            >
                <b-spinner
                    v-if="loadingCreateResponse"
                    key="loadingCreate"
                    small
                ></b-spinner>
                <div 
                    v-else
                    key="notLoadingCreate"
                    class="d-flex justify-content-center align-items-center font-weight-600"
                >
                    View Details & Next Steps
                    <b-icon 
                        icon="arrow-right" 
                        class="ml-3"
                        font-scale="1.2"
                    ></b-icon>
                </div>
            </b-button>
        </transition>

        <!-- QUICKLINK REGISTER BUTTON (redirect to register) -->
        <transition name="fade-fast" mode="out-in">
            <b-button 
                v-if="quicklinkCanRegister"
                id="quicklink-register-button"
                variant="primary"
                block
                class="text-uppercase py-2 mt-3 shadow-sm" 
                :href="$splUrlMap.tenant_specific_signup"
            >
                <div class="text-uppercase font-weight-600 mb-1">Respond Now</div>
                <div class="small">
                    Login / Register
                    <b-icon icon="arrow-right" class="ml-1"></b-icon>
                </div>            
            </b-button>
        </transition>

        <transition name="fade-fast" mode="out-in">
            <div v-if="!responseIsStarted">
                <!-- the BEFORE SUBMIT BUTTONS - big and easy to use  -->

                <!-- TRANSITION BUTTONS -->
                <b-button
                    v-for="trans in possibleTransitions"
                    :key="trans.trigger"
                    :variant="trans.color_variant"
                    block
                    class="text-uppercase py-3 mt-3 shadow-sm" 
                    @click="callTransitionTrigger(trans.trigger)"
                    v-b-tooltip.hover.left
                    :title="trans.description"
                >
                    <b-spinner
                        v-if="loadingTransition"
                        :key="`loading-${trans.trigger}`"
                        small
                    ></b-spinner>
                    <div 
                        v-else
                        :key="`not-loading-${trans.trigger}`"
                        class="d-flex justify-content-center align-items-center font-weight-600"
                    >
                        {{ trans.label }}
                        <b-icon 
                            :icon="trans.icon" 
                            class="ml-3"
                            font-scale="1.2"
                        ></b-icon>
                    </div>
                </b-button>

                <!-- VIEW RESPONSE BUTTON -->
                <b-button
                    v-if="responseUrl && responseIsStarted"
                    variant="primary"
                    block
                    class="text-uppercase py-3 mt-3 shadow-sm" 
                    :href="responseUrl"
                >
                    <div 
                        class="d-flex justify-content-center align-items-center font-weight-600"
                    >
                        View / Update Response
                        <b-icon 
                            icon="arrow-right" 
                            class="ml-3"
                            font-scale="1.2"
                        ></b-icon>
                    </div>
                </b-button>

                <!-- MESSAGE / QUESTION BUTTON -->
                <b-button
                    v-if="vendorMessagesUrl"
                    variant="secondary"
                    block
                    class="text-uppercase py-3 mt-3 shadow-sm" 
                    :href="vendorMessagesUrl"
                >
                    <div 
                        class="d-flex justify-content-center align-items-center font-weight-600"
                    >
                        {{ responseIsStarted ? 'Messages' : 'Ask Questions' }}
                        <b-icon 
                            icon="chat-text" 
                            class="ml-3"
                            font-scale="1.2"
                        ></b-icon>
                    </div>
                </b-button>
            </div>
        </transition>

        <!-- <transition name="fade-fast" mode="out-in"> -->  <!-- removing transition... makes things flash on submits, not great -->
            <div v-if="responseIsStarted && possibleTransitions.length">
                <!-- show things differently if response already started - smaller and more subtle buttons -->
                <div class="font-weight-bold mb-1">Actions</div>
                <b-list-group>
                    <b-list-group-item
                        v-for="trans in possibleTransitions"
                        :key="trans.trigger"
                        button
                        @click="callTransitionTrigger(trans.trigger)"
                    >
                        <b-spinner
                            v-if="loadingTransition"
                            :key="`loading-${trans.trigger}`"
                            small
                        ></b-spinner>
                        <div 
                            v-else
                            :key="`not-loading-${trans.trigger}`"
                            class="font-weight-600"
                        >
                            <div>{{ trans.label }}</div>
                            <div class="small text-muted">{{ trans.description }}</div>
                        </div>
                    </b-list-group-item>
                </b-list-group>
            </div>
        <!-- </transition> -->

        <state-machine-transitions
            ref="transitionsComponent"
            @state-change="onStateChange"
            @loading="handleStateMachineLoading"
            @cancel="onStateChangeCancel"
        ></state-machine-transitions>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
// this goes on the Rfx Detail page in the portal
// will show things conditionally based on the current TU's status with this RFX
// and the status of the RFX, whether open to portal responses, etc.
// its call RfxResponseActions... but can/should be used even before a RfxResponse is created -- can do RfxResponse transitions, AND create responses

import { errorHandling, currentUserInfoMixin, splUrlMapMixin } from '../../js/custom-vue-mixins';
import { RfxResponse } from '../../js/models/rfxResponse';

export default {
    name: 'PortalRfxResponseActions',
    components: {},
    mixins: [
        errorHandling,
        currentUserInfoMixin,
        splUrlMapMixin,  // has computed $splUrlMap
    ],
    props: {
        rfxId: {
            type: String,
            required: true,
        },
        quicklinkSlug: {
            // must provide this (and pass in API call config.params.quicklink_slug) if its a quicklink view
            // ONLY populate it if it IS a quicklink view, or else endpoints will not check TU and it will deny them
            type: String,
            default: '',
        },
        // maybe pass in an array of triggers you want to HIDE?  
        // because parent is handling them differently??? 
        // since you are emitting list of availableTriggers to parent...
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loadingGetActions: false,
            loadingCallAction: false,
            loadingCreateResponse: false,
            loadingTransition: false,  // gets updated from the transition component via handler
            respData: {},  // will stash the found response here if we have one
            canCreate: false,  // will set this with results of API call
            quicklinkCanRegister: false,  // will set from API results, if the user is not logged in but things are OK to register and submit a bid
        }
    },
    computed: {
        possibleTransitions() {  // array of transition objects
            // pull these out of respData if we have them, otherwise return empty array
            // strange that you have this as computed property, but availableTriggers (just a mapped property of this) in data 
            // why?  because the nextTick delay for this is OK here, but we don't want it for availableTriggers since we're emitting that...
            return this.respData?.possible_transitions ?? [];
        },
        responseUrl() {
            // pull out the response URL to top level, if we have one
            return this.respData?.vendor_response_url ?? this.respData.vendor_url;
        },
        vendorMessagesUrl() {
            return this.respData?.vendor_messages_url;
        },
        showDummyCreateButton() {
            return this.$currentUserInfo.is_internal;
        },
        responseIsStarted() {
            // return true if the response is started (or later)
            return this.respData?.is_submitted || this.respData?.sm_state === 'started';
        },
        isQuicklinkView() {
            return !!this.quicklinkSlug;
        },
    },
    async mounted() {
        await this.getActions();
    },
    methods: {
        // PUBLIC METHODS (used internally too, but intended to be used from parent via parent.$refs as well)
        refresh() {
            // refresh the list of actions -- will emit availability when done, so watch those events (update-can-create, update-available-triggers)
            console.log('HELLO FROM refresh method');
            this.getActions();  // no need for async/await here... just trigger it, then watch for emitted things
        },
        // if needed, you can use createResponse() and callTransitionTrigger externally too
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getActions() {
            this.loadingGetActions = true;
            // NOTE - this serves EXTERNAL and INTERNAL and QUICKLINK views 
            // MUST PASS IN 'quicklink_slug' in config.params with quicklink slug value to show actions for quicklinks (like going to create new account, etc.)
            let requestConfig = null;
            if (this.isQuicklinkView) {
                requestConfig = {params: {quicklink_slug: this.quicklinkSlug}};
            }
            try {
                const { data } = await suppliosApi.rfx.get_actions_for_tu(this.rfxId, requestConfig);
                console.log(data);  // data will have keys { response, can_create }
                // MAY NOT HAVE RESPONSE!  might be null
                this.respData = data.response ? new RfxResponse(data.response) : {};
                this.canCreate = data.can_create || false;
                this.quicklinkCanRegister = data.quicklink_can_register || false;
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.emitAvailability();  // tell parent what's available
            this.loadingGetActions = false;
        },
        async createResponse() {  // can call this from this component, or from parent
            // hit API to create a new response.
            // if successful, redirect to response URL
            this.loadingCreateResponse = true;
            try {
                const { data } = await suppliosApi.rfx.createPortalResponse(this.rfxId);
                // it will send back the entire newly created RfxResponse object
                const createdResponse = new RfxResponse(data);
                this.handleCreateSuccess(createdResponse);
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.emitAvailability();  // tell parent what's available - although we won't get here... we redirect first...
            this.loadingCreateResponse = false;
        },
        handleCreateSuccess: function(newRespData) {
            let redirectUrl = newRespData.vendor_response_url || newRespData.vendor_url;  // new serializer version + RfxResponse() instance will use .vendor_url
            // just go to the response vendor_url -- that's safest, so you can change things on backend and it will work here
            // we should have already verified the URL exists before passing in here...
            window.location.href = redirectUrl;  // THE REDIRECT
        },
        // HANDLING TRANSITIONS
        callTransitionTrigger(triggerName) {  // can call this from this component, or from parent
            console.log(`hello from callTransitionTrigger - will call ${triggerName}`);
            // this calls the transition using the StateMachineTransitions component via $refs
            // we must pass the entire transitionObject, the suppliosApi endpoint string, and the instance ID that we want to transition
            // so we have to look some things up first if we're only passing in the trigger name
            const transComponent = this.$refs['transitionsComponent'];
            if (!transComponent) return;
            //const transitionObject = this.respData.possible_transitions.find(trans => trans.trigger === triggerName);
            const transitionObject = this.possibleTransitions.find(trans => trans.trigger === triggerName);
            if (!transitionObject) {
                this.$toast.error('That action is not available.  Please contact Supplios Support.');
                return;
            }
            const callTransitionEndpoint = 'rfxResponse.call_transition';
            transComponent.trigger(transitionObject, callTransitionEndpoint, this.respData.id);
            // then that component will emit 'state-change' with newState (string name of state)
            // handle that in other callback below
        },
        onStateChange(stateChangeResponse) {
            // called when handling emitted 'state-change' event from StateMachineTransitions component (after it did the transition)
            // stateChangeResponse will be { success, message, object, redirect_url (might be '') }
            // for RfxResponse, we should at least have these things in object: id, pk, sm_state, state_dict, (and maybe vendor_url... but that might be a dead-end if state just changed to something where they can't see that page)
            console.log(`state changed - apiResponse below`);
            console.log(stateChangeResponse);
            // also must emit @updated to tell parent list if we're in ManageResponses
            // NOTE Oct 5 2022 - DO WE HAVE TO UPDATE LOCAL DATA AFTER THE TRANSITION? CALL REFRESH? AVAILABILITY LIKELY CHANGED!?!? but we never got new respData
            // currently the parent just responds to the 'updated' event and destroys/recreates this component, so it's not a big deal... but if it doesn't do that, then emitAvailability will be wrong
            // NOT CALLING THIS FOR NOW - UNTIL YOU HANDLE REFRESING LOCAL DATA: this.emitAvailability();  // tell parent what's available
            this.emitUpdated(stateChangeResponse);  // just pass along the payload...
            // TODO - do we also need to re-call getActions to get new list of actions available from current state????
            // if parent destroys us, that will happen when re-mounted... that's what we're currently relying on
        },
        onStateChangeCancel() {
            // called anytime the state change modal / process is cancelled
            console.log('state change cancelled');
        },
        emitUpdated(stateChangeResponsePayload) {
            // emit 'updated' event to parent to reload list, etc.
            // we'll pass along the payload -- parent likely wants to know the new state
            this.$emit('updated', stateChangeResponsePayload);  // { success, message, object, redirect_url (might be '') }
        },
        emitAvailability() {
            // emit things to parent so it knows what's available!
            // putting all the logic here -- so just make sure you already have things set in data to pull from
            const possible_transitions = this.respData?.possible_transitions ?? [];  // don't get from this.possibleTransitions... that's a computed, so slow to update
            const availableTriggers = possible_transitions.map(trans => trans.trigger);
            // we're doing 3 separate emits because seems right to keep all the transitions separate
            this.$emit('update-can-create', this.canCreate);
            this.$emit('update-available-triggers', availableTriggers);
            this.$emit('update-quicklink-can-register', this.quicklinkCanRegister);
        },
        handleStateMachineLoading(loadingState) {
            // handler for @loading on transition component - payload will be boolean of its loading state
            this.loadingTransition = loadingState;
        },

    }
}
</script>

<style>

</style>